/** STYLE — the Text Style Manager, and a live sample of what the canvas will actually draw.
 *
 * ⚠⚠⚠ **THE RIBBON'S "Text Style…" OPENED THE DESIGN APP'S PEN STYLES PALETTE.** Its note said
 * *"height, pen, weight and dash live on a style here, not on a separate text style"* — which
 * is true of the app this chrome was lifted from and false of this one. In a DXF a `STYLE` is a
 * symbol-table row naming a FONT FILE, a fixed height, a width factor and an oblique angle, and
 * **972 of them are stored on this box across 57 distinct fonts**. A pen style cannot express
 * any of it. Same class of mistake as greying GROUP because "a group here is a BLOCK".
 *
 * ⚠⚠ **THE SAMPLE IS THE HONEST HALF.** A style can name `vni-helve.ttf`, and whether the
 * machine looking at the drawing has that font is not something the drawing can know. Drawing
 * the sample in the stack the canvas resolved gives the drafter the answer at a glance, instead
 * of leaving them to wonder why a note looks wrong. See `backend/drawing/textstyles.py`.
 *
 * ⚠ Sorted by USE, like the Dimension Style Manager. A client drawing arrives with 43 styles
 * and uses four of them — measured on drawing 50, whose top style carries 1,946 pieces of text
 * and whose alphabetical first carries none.
 */
import { useEffect, useState } from "react";

import { loadTextStyles, type TextStyleRow } from "./api";

/** ⚠ The four a drafter changes, in AutoCAD's own order. `bigfont` is offered because this
 *  office's drawings are trilingual and an SHX style needs one for anything outside ASCII. */
const FIELDS: [string, string, "text" | "num"][] = [
  ["font", "Font file", "text"],
  ["bigfont", "Big font (CJK/VN)", "text"],
  ["height", "Fixed height (0 = ask)", "num"],
  ["width", "Width factor", "num"],
  ["oblique", "Oblique angle °", "num"],
];

export default function TextStyleDialog({ drawingId, onApply, onClose }: {
  drawingId: number;
  onApply: (name: string, vars: Record<string, string | number>) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<TextStyleRow[]>([]);
  const [pick, setPick] = useState("");
  const [vals, setVals] = useState<Record<string, string>>({});
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadTextStyles(drawingId).then((j) => {
      const sorted = [...j.styles].sort((a, b) => (b.used - a.used)
                                                  || a.name.localeCompare(b.name));
      setRows(sorted);
      if (sorted.length) choose(sorted[0]);
    }).catch(() => setErr("could not read this drawing's text styles"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on open
  }, [drawingId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const choose = (r: TextStyleRow) => {
    setPick(r.name);
    setName(r.name);
    setVals({ font: r.font, bigfont: r.bigfont, height: String(r.height),
              width: String(r.width), oblique: String(r.oblique) });
    setErr(null);
  };

  const cur = rows.find((r) => r.name === pick);
  const wf = Number(vals.width);
  const ob = Number(vals.oblique);

  const go = () => {
    const n = name.trim();
    if (!n) return setErr("A text style needs a name.");
    const out: Record<string, string | number> = {};
    for (const [k, label, kind] of FIELDS) {
      const raw = (vals[k] ?? "").trim();
      if (kind === "text") { if (raw || k === "bigfont") out[k] = raw; continue; }
      if (!raw) continue;
      const v = Number(raw);
      if (!Number.isFinite(v)) return setErr(`${label} must be a number.`);
      out[k] = v;
    }
    if (Number(out.width ?? 1) <= 0) return setErr("A width factor must be greater than zero.");
    onApply(n, out);
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "grid", placeItems: "center", zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 600, maxHeight: "90%", display: "flex", flexDirection: "column",
                    background: "#3c3f41", border: "1px solid #555", borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          Text Style Manager
        </div>
        <div style={{ padding: 12, display: "flex", gap: 12, font: "11.5px system-ui",
                      minHeight: 0 }}>
          <div style={{ width: 190, display: "flex", flexDirection: "column", gap: 4,
                        minHeight: 0 }}>
            <span style={{ color: "#9aa0a6" }}>Styles ({rows.length})</span>
            <div data-testid="textstyle-list"
                 style={{ flex: 1, overflow: "auto", background: "#2b2d2f",
                          border: "1px solid #555", minHeight: 120, maxHeight: 280 }}>
              {rows.map((r) => (
                <div key={r.name} onClick={() => choose(r)}
                     data-testid={`textstyle-${r.name}`}
                     style={{ padding: "3px 7px", cursor: "pointer",
                              background: r.name === pick ? "#3c5a82" : "transparent" }}>
                  {r.name}
                  <span style={{ color: "#9aa0a6", marginLeft: 6 }}>
                    {r.used ? `· ${r.used}` : ""}
                  </span>
                </div>))}
            </div>
          </div>

          <div style={{ flex: 1, display: "grid", gap: 8, alignContent: "start" }}>
            <label style={{ display: "grid", gap: 3 }}>
              <span style={{ color: "#9aa0a6" }}>Name</span>
              <input value={name} data-testid="textstyle-name" style={inp}
                     onChange={(e) => { setName(e.target.value); setErr(null); }} />
            </label>
            {FIELDS.map(([k, label]) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#9aa0a6", width: 168 }}>{label}</span>
                <input value={vals[k] ?? ""} data-testid={`textstyle-${k}`}
                       onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))}
                       style={{ ...inp, flex: 1 }} />
              </label>))}

            {/* ⚠⚠ **A LIVE SAMPLE IN THE STACK THE CANVAS RESOLVED, NOT IN THE FONT THE STYLE
                NAMES.** The two are different whenever the machine does not have the font, and
                that difference is the single most useful thing this dialog can show. The width
                factor is a horizontal SCALE — there is no CSS property for 0.7 — so the sample
                is transformed exactly the way `Canvas2D` transforms the real text. */}
            <div style={{ background: "#2b2d2f", border: "1px solid #555", padding: "10px 8px",
                          overflow: "hidden" }}>
              <div style={{ font: `${cur?.italic ? "italic " : ""}`
                                  + `${cur && cur.weight !== 400 ? `${cur.weight} ` : ""}`
                                  + `20px ${cur?.css ?? "sans-serif"}`,
                            transform: `scaleX(${Number.isFinite(wf) && wf > 0 ? wf : 1})`
                                       + `${Number.isFinite(ob) && ob ? ` skewX(${-ob}deg)` : ""}`,
                            transformOrigin: "left center", whiteSpace: "nowrap" }}>
                MẶT BẰNG · AaBbCc 123
              </div>
            </div>
            <div style={{ color: "#9aa0a6", fontSize: 11, lineHeight: 1.5 }}>
              {cur ? <>Drawn with <b>{(cur.css.split(",")[0] || "").replace(/"/g, "")}</b>.{" "}
                     {cur.font.toLowerCase().endsWith(".shx") || !cur.font.includes(".")
                       ? "⚠ An SHX is a single-stroke font with no web equivalent; the nearest "
                         + "metric is substituted."
                       : ""}</> : null}
              {" "}⚠ The width factor here is the DEFAULT for new text — a label's own overrides
              it, which is how 49.2% of this office's text is condensed while 926 of its 972
              styles are 1.0.
            </div>
            {err && <div data-testid="textstyle-error" style={{ color: "#ff8f8f" }}>{err}</div>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end",
                      padding: "0 12px 12px" }}>
          <button style={btn} onClick={onClose} data-testid="textstyle-cancel">Cancel</button>
          <button style={{ ...btn, background: "#3c5a82", borderColor: "#4a6d9c" }}
                  data-testid="textstyle-ok" onClick={go}>Apply</button>
        </div>
      </div>
    </div>);
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "3px 6px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
